import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// jsPDF için tip genişletmesi
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable: {
      finalY: number;
    };
  }
}

// Türkçe karakter desteği için font encoding
const setupPDF = () => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    putOnlyUsedFonts: true,
    compress: true,
  });
  
  // Use courier font which has better Unicode support
  doc.setFont('courier', 'normal');
  
  return doc;
};

// Tarih formatı
const formatDate = (date: string | Date) => {
  const d = new Date(date);
  const day = d.getDate();
  const months = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 
                  'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  
  return `${day} ${month} ${year}`;
};

// Tarih ve saat formatı
const formatDateTime = (date: string | Date) => {
  const d = new Date(date);
  const day = d.getDate();
  const months = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 
                  'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  
  return `${day} ${month} ${year} ${hours}:${minutes}`;
};

// PDF başlık ve footer
const addHeader = (doc: jsPDF, title: string, pageNumber: number, totalPages: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Başlık
  doc.setFontSize(16);
  doc.setFont('courier', 'bold');
  doc.text(title, pageWidth / 2, 15, { align: 'center' });

  // Sayfa numarası
  doc.setFontSize(8);
  doc.setFont('courier', 'normal');
  doc.text(`Sayfa ${pageNumber} / ${totalPages}`, pageWidth - 15, pageHeight - 10, { align: 'right' });
};

// İmza alanları
const addSignatureSection = (doc: jsPDF, yPosition: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftX = 20;
  const rightX = pageWidth / 2 + 10;
  const boxWidth = (pageWidth - 50) / 2;
  const boxHeight = 35;

  doc.setFontSize(10);
  doc.setFont('courier', 'bold');

  // Teslim Eden
  doc.text('TESLIM EDEN', leftX, yPosition);
  doc.rect(leftX, yPosition + 2, boxWidth, boxHeight);
  
  doc.setFont('courier', 'normal');
  doc.setFontSize(9);
  doc.text('Ad Soyad:', leftX + 2, yPosition + 10);
  doc.line(leftX + 20, yPosition + 10, leftX + boxWidth - 2, yPosition + 10);
  
  doc.text('Tarih:', leftX + 2, yPosition + 18);
  doc.line(leftX + 20, yPosition + 18, leftX + boxWidth - 2, yPosition + 18);
  
  // İmza için boşluk bırak
  doc.text('Imza:', leftX + 2, yPosition + 32);

  // Teslim Alan
  doc.setFont('courier', 'bold');
  doc.setFontSize(10);
  doc.text('TESLIM ALAN', rightX, yPosition);
  doc.rect(rightX, yPosition + 2, boxWidth, boxHeight);
  
  doc.setFont('courier', 'normal');
  doc.setFontSize(9);
  doc.text('Ad Soyad:', rightX + 2, yPosition + 10);
  doc.line(rightX + 20, yPosition + 10, rightX + boxWidth - 2, yPosition + 10);
  
  doc.text('Tarih:', rightX + 2, yPosition + 18);
  doc.line(rightX + 20, yPosition + 18, rightX + boxWidth - 2, yPosition + 18);
  
  // İmza için boşluk bırak
  doc.text('Imza:', rightX + 2, yPosition + 32);

  return yPosition + boxHeight + 10;
};

// Durum çevirisi
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    'Available': 'Subede',
    'Assigned': 'Zimmetli',
    'InRepair': 'Onarimda',
    'Retired': 'Kullanim Disi',
    'Lost': 'Kayip',
    'Scrapped': 'Hurda',
  };
  return statusMap[status] || status;
};

// Kondisyon çevirisi
const translateCondition = (condition: string): string => {
  const conditionMap: Record<string, string> = {
    'New': 'Yeni',
    'Good': 'Iyi',
    'Fair': 'Orta',
    'Poor': 'Kotu',
    'Broken': 'Bozuk',
  };
  return conditionMap[condition] || condition;
};

export default {
  setupPDF,
  formatDate,
  formatDateTime,
  addHeader,
  addSignatureSection,
  translateStatus,
  translateCondition,
};

// Şube Raporu PDF
export const generateBranchReportPDF = (data: any) => {
  const doc = setupPDF();
  const title = 'Sube Bazli Cihaz Raporu';
  let yPos = 25;

  // Başlık
  addHeader(doc, title, 1, 1);

  // Özet bilgiler
  yPos = 25;
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('OZET BILGILER', 15, yPos);
  
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Toplam Sube: ${data.summary.totalBranches}`, 15, yPos);
  doc.text(`Toplam Cihaz: ${data.summary.totalDevices}`, 100, yPos);

  yPos += 10;

  // Her şube için detaylar
  data.data.forEach((branch: any) => {
    // Yeni sayfa kontrolü
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Şube başlığı
    doc.setFillColor(240, 240, 255);
    doc.rect(15, yPos - 5, 180, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('courier', 'bold');
    doc.text(branch.branchName, 17, yPos);
    
    yPos += 3;
    doc.setFontSize(9);
    doc.setFont('courier', 'normal');
    doc.text(`Toplam: ${branch.totalDevices} | Subede: ${branch.available} | Kulanimda: ${branch.inUse} | Onarimda: ${branch.underRepair} | Hurda: ${branch.scrapped}`, 17, yPos);
    
    yPos += 7;

    // Kategoriler ve cihazlar
    if (branch.categories && branch.categories.length > 0) {
      branch.categories.forEach((category: any) => {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }

        // Kategori başlığı
        doc.setFontSize(10);
        doc.setFont('courier', 'bold');
        doc.text(`  ${category.categoryName} (${category.totalDevices} cihaz)`, 17, yPos);
        yPos += 5;

        // Cihaz tablosu
        const tableData = category.devices.map((device: any) => [
          device.serialNumber || '-',
          device.productName || '-',
          device.brand || '-',
          device.model || '-',
          translateStatus(device.status),
          translateCondition(device.condition || 'Good'),
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Seri No', 'Urun', 'Marka', 'Model', 'Durum', 'Kondisyon']],
          body: tableData,
          theme: 'grid',
          styles: { 
            fontSize: 8,
            cellPadding: 2,
            font: 'courier',
          },
          headStyles: {
            fillColor: [200, 200, 200],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            font: 'courier',
          },
          margin: { left: 20, right: 15 },
        });

        yPos = doc.lastAutoTable.finalY + 5;
      });
    }

    yPos += 5;
  });

  // İmza alanları
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  addSignatureSection(doc, yPos);

  // PDF'i indir
  doc.save(`Sube_Raporu_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Kategori Raporu PDF
export const generateCategoryReportPDF = (data: any) => {
  const doc = setupPDF();
  const title = 'Kategori Bazli Cihaz Raporu';
  let yPos = 25;

  addHeader(doc, title, 1, 1);

  // Özet bilgiler
  yPos = 25;
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('OZET BILGILER', 15, yPos);
  
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Toplam Kategori: ${data.summary.totalCategories}`, 15, yPos);
  doc.text(`Toplam Cihaz: ${data.summary.totalDevices}`, 100, yPos);

  yPos += 10;

  // Her kategori için detaylar
  data.data.forEach((category: any) => {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Kategori başlığı
    doc.setFillColor(240, 240, 255);
    doc.rect(15, yPos - 5, 180, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('courier', 'bold');
    doc.text(category.categoryName, 17, yPos);
    
    yPos += 3;
    doc.setFontSize(9);
    doc.setFont('courier', 'normal');
    doc.text(`Toplam: ${category.totalDevices} | Subede: ${category.available} | Kulanimda: ${category.inUse} | Onarimda: ${category.underRepair} | Hurda: ${category.scrapped}`, 17, yPos);
    
    yPos += 7;

    // Cihaz tablosu
    const tableData = category.devices.map((device: any) => [
      device.serialNumber || '-',
      device.productName || '-',
      device.brand || '-',
      device.model || '-',
      translateStatus(device.status),
      translateCondition(device.condition || 'Good'),
      device.branch || '-',
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Seri No', 'Urun', 'Marka', 'Model', 'Durum', 'Kondisyon', 'Sube']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 7,
        cellPadding: 1.5,
        font: 'courier',
      },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7,
        font: 'courier',
      },
      margin: { left: 15, right: 15 },
    });

    yPos = doc.lastAutoTable.finalY + 8;
  });

  // İmza alanları
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  addSignatureSection(doc, yPos);

  doc.save(`Kategori_Raporu_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Personel Raporu PDF
export const generatePersonnelReportPDF = (data: any) => {
  const doc = setupPDF();
  const title = 'Personel Bazli Cihaz Raporu';
  let yPos = 25;

  addHeader(doc, title, 1, 1);

  // Özet bilgiler
  yPos = 25;
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('OZET BILGILER', 15, yPos);
  
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Toplam Personel: ${data.summary.totalPersonnel}`, 15, yPos);
  doc.text(`Toplam Zimmetli Cihaz: ${data.summary.totalDevices}`, 100, yPos);

  yPos += 10;

  // Her personel için detaylar
  data.data.forEach((personnel: any) => {
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }

    // Personel başlığı
    doc.setFillColor(240, 240, 255);
    doc.rect(15, yPos - 5, 180, 12, 'F');
    doc.setFontSize(11);
    doc.setFont('courier', 'bold');
    doc.text(personnel.personnelName, 17, yPos);
    
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('courier', 'normal');
    doc.text(`Email: ${personnel.email} | Tel: ${personnel.phone}`, 17, yPos);
    yPos += 4;
    doc.text(`Departman: ${personnel.department} | Zimmetli Cihaz: ${personnel.totalDevices}`, 17, yPos);
    
    yPos += 7;

    // Kategoriler ve cihazlar
    if (personnel.categories && personnel.categories.length > 0) {
      personnel.categories.forEach((category: any) => {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(10);
        doc.setFont('courier', 'bold');
        doc.text(`  ${category.categoryName} (${category.totalDevices} cihaz)`, 17, yPos);
        yPos += 5;

        const tableData = category.devices.map((device: any) => [
          device.serialNumber || '-',
          device.productName || '-',
          device.brand || '-',
          device.model || '-',
          translateStatus(device.status),
          device.assignedDate ? formatDate(device.assignedDate) : '-',
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Seri No', 'Urun', 'Marka', 'Model', 'Durum', 'Zimmet Tarihi']],
          body: tableData,
          theme: 'grid',
          styles: { 
            fontSize: 8,
            cellPadding: 2,
            font: 'courier',
          },
          headStyles: {
            fillColor: [200, 200, 200],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            font: 'courier',
          },
          margin: { left: 20, right: 15 },
        });

        yPos = doc.lastAutoTable.finalY + 5;
      });
    }

    yPos += 5;
  });

  // İmza alanları
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  addSignatureSection(doc, yPos);

  doc.save(`Personel_Raporu_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Şirket Raporu PDF
export const generateCompanyReportPDF = (data: any) => {
  const doc = setupPDF();
  const title = 'Sirket Bazli Cihaz Raporu';
  let yPos = 25;

  addHeader(doc, title, 1, 1);

  // Özet bilgiler
  yPos = 25;
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('OZET BILGILER', 15, yPos);
  
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Toplam Sirket: ${data.summary.totalCompanies}`, 15, yPos);
  doc.text(`Toplam Cihaz: ${data.summary.totalDevices}`, 100, yPos);

  yPos += 10;

  // Her şirket için detaylar
  data.data.forEach((company: any) => {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Şirket başlığı
    doc.setFillColor(240, 240, 255);
    doc.rect(15, yPos - 5, 180, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('courier', 'bold');
    doc.text(company.companyName, 17, yPos);
    
    yPos += 3;
    doc.setFontSize(9);
    doc.setFont('courier', 'normal');
    doc.text(`Toplam: ${company.totalDevices} | Subede: ${company.available} | Kulanimda: ${company.inUse} | Onarimda: ${company.underRepair} | Hurda: ${company.scrapped}`, 17, yPos);
    
    yPos += 7;

    // Cihaz tablosu
    const tableData = company.devices.map((device: any) => [
      device.serialNumber || '-',
      device.productName || '-',
      device.brand || '-',
      device.model || '-',
      translateStatus(device.status),
      translateCondition(device.condition || 'Good'),
      device.branch || '-',
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Seri No', 'Urun', 'Marka', 'Model', 'Durum', 'Kondisyon', 'Sube']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 7,
        cellPadding: 1.5,
        font: 'courier',
      },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7,
        font: 'courier',
      },
      margin: { left: 15, right: 15 },
    });

    yPos = doc.lastAutoTable.finalY + 8;
  });

  // İmza alanları
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  addSignatureSection(doc, yPos);

  doc.save(`Sirket_Raporu_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Bilgisayar Raporu PDF
export const generateComputerReportPDF = (devices: any[]) => {
  const doc = setupPDF();
  const title = 'Bilgisayar Raporu';
  let yPos = 25;

  addHeader(doc, title, 1, 1);

  // Özet bilgiler
  yPos = 25;
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('OZET BILGILER', 15, yPos);
  
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Toplam Bilgisayar: ${devices.length}`, 15, yPos);

  yPos += 10;

  // Cihaz tablosu
  const tableData = devices.map((device: any) => [
    device.serialNumber || '-',
    device.specs?.processor || '-',
    device.specs?.disk || '-',
    device.specs?.ram || '-',
    translateStatus(device.status),
    device.branch?.name || '-',
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Seri No', 'Islemci', 'Disk', 'RAM', 'Durum', 'Sube']],
    body: tableData,
    theme: 'grid',
    styles: { 
      fontSize: 8,
      cellPadding: 2,
      font: 'courier',
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      font: 'courier',
    },
    margin: { left: 15, right: 15 },
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // İmza alanları
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  addSignatureSection(doc, yPos);

  doc.save(`Bilgisayar_Raporu_${new Date().toISOString().split('T')[0]}.pdf`);
};


// Servis Teslim Formu PDF
export const generateRepairFormPDF = (data: {
  formNumber: string;
  date: string;
  deviceInfo: {
    serialNumber: string;
    assetTag: string;
    productName: string;
    brand: string;
    model: string;
    condition: string;
  };
  serviceProvider: {
    name: string;
    contactPerson?: string;
    phone?: string;
    address?: string;
  };
  repairDetails: {
    faultDescription: string;
    accessories?: string;
  };
  delivery: {
    deliveryDate: string;
    deliveredBy: string;
    receivedBy?: string;
  };
  branch?: {
    name: string;
    address?: string;
    phone?: string;
  };
  notes?: string;
}) => {
  const doc = setupPDF();
  
  // Use courier font for Turkish character support
  doc.setFont('courier', 'normal');
  
  const title = 'SERVIS TESLIM FORMU';
  let yPos = 30;

  // Başlık
  doc.setFontSize(18);
  doc.setFont('courier', 'bold');
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.text(title, pageWidth / 2, yPos, { align: 'center' });

  yPos += 8;

  // Form numarası
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(`Form No: ${data.formNumber}`, 15, yPos);
  doc.text(`Tarih: ${data.date}`, pageWidth - 15, yPos, { align: 'right' });

  yPos += 8;

  // Şube bilgileri (varsa)
  if (data.branch) {
    const branchLines = 2 + (data.branch.address ? 1 : 0) + (data.branch.phone ? 1 : 0);
    const branchBoxHeight = 8 + (branchLines * 5);
    
    doc.setFillColor(240, 240, 255);
    doc.rect(15, yPos - 5, 180, branchBoxHeight, 'F');
    doc.setFontSize(11);
    doc.setFont('courier', 'bold');
    doc.text('SUBE BILGILERI', 17, yPos);
    
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('courier', 'normal');
    doc.text(`Sube: ${data.branch.name}`, 17, yPos);
    yPos += 5;
    if (data.branch.address) {
      doc.text(`Adres: ${data.branch.address}`, 17, yPos);
      yPos += 5;
    }
    if (data.branch.phone) {
      doc.text(`Telefon: ${data.branch.phone}`, 17, yPos);
      yPos += 5;
    }
    yPos += 5;
  }

  // Cihaz bilgileri
  doc.setFillColor(240, 240, 255);
  doc.rect(15, yPos - 5, 180, 35, 'F');
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('CIHAZ BILGILERI', 17, yPos);
  
  yPos += 6;
  doc.setFontSize(9);
  doc.setFont('courier', 'normal');
  
  // Ürün adı (tek satır, uzunsa kısalt)
  const productName = data.deviceInfo.productName.length > 80 
    ? data.deviceInfo.productName.substring(0, 77) + '...' 
    : data.deviceInfo.productName;
  doc.text(`Urun: ${productName}`, 17, yPos);
  yPos += 5;
  
  // Brand - ObjectId kontrolü
  let brandText = data.deviceInfo.brand;
  if (brandText && brandText.length === 24 && /^[0-9a-fA-F]{24}$/.test(brandText)) {
    brandText = 'Belirtilmemis';
  }
  doc.text(`Marka: ${brandText}`, 17, yPos);
  yPos += 5;
  
  doc.text(`Model: ${data.deviceInfo.model}`, 17, yPos);
  yPos += 5;
  
  doc.text(`Seri No: ${data.deviceInfo.serialNumber}`, 17, yPos);
  yPos += 5;
  
  doc.text(`Varlik Etiketi: ${data.deviceInfo.assetTag}`, 17, yPos);
  yPos += 5;
  
  doc.text(`Kondisyon: ${translateCondition(data.deviceInfo.condition)}`, 17, yPos);
  
  yPos += 10;

  // Servis sağlayıcı bilgileri
  doc.setFillColor(240, 240, 255);
  const serviceBoxHeight = 8 + (data.serviceProvider.contactPerson ? 5 : 0) + 
                           (data.serviceProvider.phone ? 5 : 0) + 
                           (data.serviceProvider.address ? 5 : 0);
  doc.rect(15, yPos - 5, 180, serviceBoxHeight, 'F');
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('SERVIS SAGLAYICI', 17, yPos);
  
  yPos += 6;
  doc.setFontSize(9);
  doc.setFont('courier', 'normal');
  doc.text(`Firma: ${data.serviceProvider.name}`, 17, yPos);
  yPos += 5;
  if (data.serviceProvider.contactPerson) {
    doc.text(`Yetkili: ${data.serviceProvider.contactPerson}`, 17, yPos);
    yPos += 5;
  }
  if (data.serviceProvider.phone) {
    doc.text(`Telefon: ${data.serviceProvider.phone}`, 17, yPos);
    yPos += 5;
  }
  if (data.serviceProvider.address) {
    doc.text(`Adres: ${data.serviceProvider.address}`, 17, yPos);
    yPos += 5;
  }
  
  yPos += 5;

  // Arıza detayları
  const faultLines = doc.splitTextToSize(data.repairDetails.faultDescription, 170);
  const accessoryLines = data.repairDetails.accessories ? 1 : 0;
  const faultBoxHeight = 12 + (faultLines.length * 4) + (accessoryLines * 5);
  
  doc.setFillColor(255, 240, 240);
  doc.rect(15, yPos - 5, 180, faultBoxHeight, 'F');
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('ARIZA DETAYLARI', 17, yPos);
  
  yPos += 6;
  doc.setFontSize(9);
  doc.setFont('courier', 'normal');
  
  // Arıza açıklaması
  doc.text('Ariza Aciklamasi:', 17, yPos);
  yPos += 5;
  doc.text(faultLines, 17, yPos);
  yPos += faultLines.length * 4;
  
  if (data.repairDetails.accessories) {
    yPos += 2;
    doc.text(`Aksesuarlar: ${data.repairDetails.accessories}`, 17, yPos);
    yPos += 5;
  }
  
  yPos += 5;

  // Teslim bilgileri
  doc.setFillColor(240, 255, 240);
  doc.rect(15, yPos - 5, 180, 20, 'F');
  doc.setFontSize(11);
  doc.setFont('courier', 'bold');
  doc.text('TESLIM BILGILERI', 17, yPos);
  
  yPos += 6;
  doc.setFontSize(9);
  doc.setFont('courier', 'normal');
  doc.text(`Teslim Tarihi: ${data.delivery.deliveryDate}`, 17, yPos);
  yPos += 5;
  doc.text(`Teslim Eden: ${data.delivery.deliveredBy}`, 17, yPos);
  yPos += 5;
  if (data.delivery.receivedBy) {
    doc.text(`Teslim Alan: ${data.delivery.receivedBy}`, 17, yPos);
    yPos += 5;
  }
  
  yPos += 5;

  // Notlar (varsa ve "eksik yoktur" değilse)
  if (data.notes && data.notes.trim() !== '' && !data.notes.toLowerCase().includes('eksik yoktur')) {
    const notesLines = doc.splitTextToSize(data.notes, 170);
    const notesBoxHeight = 8 + (notesLines.length * 4);
    
    doc.setFillColor(255, 255, 240);
    doc.rect(15, yPos - 5, 180, notesBoxHeight, 'F');
    doc.setFontSize(11);
    doc.setFont('courier', 'bold');
    doc.text('NOTLAR', 17, yPos);
    
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('courier', 'normal');
    doc.text(notesLines, 17, yPos);
    yPos += notesLines.length * 4 + 5;
  }

  // İmza alanları
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  addSignatureSection(doc, yPos);

  // PDF'i indir
  doc.save(`Servis_Teslim_Formu_${data.formNumber}_${new Date().toISOString().split('T')[0]}.pdf`);
};
